
import { useState, useCallback } from 'react';
import { WidgetDefinition } from '@/components/admin/dashboard/widget-types';
import { 
  fetchWidgetDefinitions as fetchDbWidgets,
  createWidgetDefinition as createDbWidget,
  updateWidgetDefinition as updateDbWidget,
  toggleWidgetActive as toggleDbWidget,
  deleteWidgetDefinition as deleteDbWidget
} from '@/services/widgets';
import { USER_WIDGET_DEFINITIONS } from '@/components/admin/dashboard/user-widgets';
import { ADMIN_WIDGET_DEFINITIONS } from '@/components/admin/dashboard/admin-widgets';
import { toast } from '@/hooks/use-toast';

// Default widget types for each dashboard
export function useWidgetOperations() {
  const [definitions, setDefinitions] = useState<WidgetDefinition[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch all widget definitions
  const fetchWidgetDefinitions = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log("Fetching widget definitions...");
      const widgetDefinitions = await fetchDbWidgets();
      console.log(`Received ${widgetDefinitions.length} widget definitions:`, 
        widgetDefinitions.map(w => ({ id: w.id, title: w.title, isActive: w.isActive })));
      setDefinitions(widgetDefinitions);
    } catch (error) {
      console.error('Error fetching widget definitions:', error);
      toast({
        title: 'Error fetching widgets',
        description: 'There was an error loading widget definitions.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize default widgets in the database
  const initializeDefaultWidgets = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get all existing widgets to avoid duplicates
      const existingWidgets = await fetchDbWidgets();
      const existingIds = existingWidgets.map(w => w.id);
      console.log("Existing widget IDs:", existingIds);
      
      const allDefaultWidgets = [
        ...ADMIN_WIDGET_DEFINITIONS,
        ...USER_WIDGET_DEFINITIONS
      ];
      
      console.log("All default widgets:", allDefaultWidgets.map(w => ({ id: w.id, title: w.title })));
      
      // Filter out widgets that already exist
      const widgetsToCreate = allDefaultWidgets.filter(w => !existingIds.includes(w.id));
      console.log("Widgets to create:", widgetsToCreate.map(w => ({ id: w.id, title: w.title })));
      
      if (widgetsToCreate.length === 0) {
        toast({
          title: 'No new widgets',
          description: 'All default widgets already exist in the database.',
        });
        setIsLoading(false);
        return;
      }
      
      // Create each widget
      for (const widget of widgetsToCreate) {
        // Make a fresh copy without the dates
        const { createdAt, updatedAt, ...widgetData } = widget;
        console.log(`Creating widget: ${widget.id}`);
        await createDbWidget(widgetData);
      }
      
      toast({
        title: 'Default widgets created',
        description: `Created ${widgetsToCreate.length} default widgets.`,
      });
      
      // Refresh the list
      await fetchWidgetDefinitions();
    } catch (error) {
      console.error('Error initializing default widgets:', error);
      toast({
        title: 'Error initializing widgets',
        description: 'There was an error creating default widgets.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWidgetDefinitions]);

  // Create a new widget definition
  const createWidgetDefinition = async (widget: WidgetDefinition): Promise<WidgetDefinition | null> => {
    try {
      const newWidget = await createDbWidget(widget);
      if (newWidget) {
        // Add to local state
        setDefinitions(prev => [...prev, newWidget]);
        toast({ 
          title: 'Widget created',
          description: `Created widget: ${widget.title}`,
        });
        return newWidget;
      }
      return null;
    } catch (error) {
      console.error('Error creating widget:', error);
      toast({
        title: 'Error creating widget',
        description: 'There was an error creating the widget.',
        variant: 'destructive',
      });
      return null;
    }
  };

  // Update an existing widget definition
  const updateWidgetDefinition = async (id: string, widgetData: Partial<WidgetDefinition>): Promise<boolean> => {
    try {
      const success = await updateDbWidget(id, widgetData);
      if (success) {
        // Update in local state
        setDefinitions(prev => 
          prev.map(widget => widget.id === id ? { ...widget, ...widgetData } : widget)
        );
        toast({ 
          title: 'Widget updated',
          description: 'Widget definition has been updated successfully.',
        });
      }
      return success;
    } catch (error) {
      console.error('Error updating widget:', error);
      toast({
        title: 'Error updating widget',
        description: 'There was an error updating the widget.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Delete a widget definition
  const deleteWidgetDefinition = async (id: string): Promise<boolean> => {
    try {
      const success = await deleteDbWidget(id);
      if (success) {
        // Remove from local state
        setDefinitions(prev => prev.filter(widget => widget.id !== id));
        toast({ 
          title: 'Widget deleted',
          description: 'Widget has been deleted successfully.',
        });
      }
      return success;
    } catch (error) {
      console.error('Error deleting widget:', error);
      toast({
        title: 'Error deleting widget',
        description: 'There was an error deleting the widget.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Toggle a widget's active status
  const toggleWidgetActive = async (id: string): Promise<boolean> => {
    try {
      // Find the current status of the widget
      const widget = definitions.find(w => w.id === id);
      if (!widget) {
        throw new Error(`Widget with id ${id} not found`);
      }
      
      const currentStatus = widget.isActive;
      const success = await toggleDbWidget(id, currentStatus);
      
      if (success) {
        // Update in local state
        setDefinitions(prev => 
          prev.map(widget => 
            widget.id === id ? { ...widget, isActive: !widget.isActive } : widget
          )
        );
        
        const newStatus = widget?.isActive ? 'inactive' : 'active';
        
        toast({ 
          title: 'Widget status updated',
          description: `Widget is now ${newStatus}.`,
        });
      }
      return success;
    } catch (error) {
      console.error('Error toggling widget active status:', error);
      toast({
        title: 'Error updating widget',
        description: 'There was an error updating the widget status.',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    definitions,
    isLoading,
    fetchWidgetDefinitions,
    initializeDefaultWidgets,
    createWidgetDefinition,
    updateWidgetDefinition,
    deleteWidgetDefinition,
    toggleWidgetActive
  };
}
